import { embeddedReport } from "hooks/PowerBi/GetEmbeddedURLs/useGetEmbeddedURLs";
import { FC, useState, memo, useMemo } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { Skeleton } from "@mui/material";
import * as styles from "./PowerBiReport.module.scss";
import ReportError from "../ReportError/ReportError";
import useGetUser from "../../../hooks/useGetUser";

type powerBiReportProps = {
  report: embeddedReport;
  onRetry?: (id: string) => void;
};

const PowerBiReport: FC<powerBiReportProps> = memo(({ report, onRetry }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const { hasCashPlanAccess, hasEmployerAccess } = useGetUser();
  const isCashplanOnlyUser = hasCashPlanAccess && !hasEmployerAccess;

  const embedConfig = useMemo(
    () => ({
      type: "report",
      embedUrl: report.embedUrl,
      accessToken: report.embedToken,
      tokenType: models.TokenType.Embed,
      pageName: report.pageId,
      settings: {
        panes: {
          filters: {
            visible: false,
          },
          pageNavigation: {
            visible: false,
          },
        },
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: models.DisplayOption.FitToPage,
        },
      },
    }),
    [report.embedUrl, report.embedToken, report.pageId]
  );

  if (error || report.error || isCashplanOnlyUser) {
    return <ReportError onRetry={() => onRetry?.(report.id)} />;
  }
  return (
    <div style={{ position: "relative" }} data-testid="powerbi-report">
      {loading && (
        <div
          data-testid="loader"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            backgroundColor: "rgba(255, 255, 255)",
          }}
        >
          <Skeleton
            height={"100%"}
            width={"100%"}
            style={{ transform: "scale(1)" }}
          />
        </div>
      )}
      <PowerBIEmbed
        getEmbeddedComponent={(embeddedReport) => {
          embeddedReport.setComponentTitle("PowerBi_IFrame_" + report.id);
        }}
        cssClassName={styles.PowerBiContainer}
        embedConfig={embedConfig}
        eventHandlers={
          new Map([
            [
              "error",
              (event: any) => {
                setLoading(false);
                setError(
                  `PowerBI Error: ${
                    event.detail?.message ??
                    event.detail?.errorCode ??
                    event.detail ??
                    "Unknown error"
                  }`
                );
              },
            ],
            [
              "rendered",
              () => {
                setLoading(false);
              },
            ],
          ])
        }
      />
    </div>
  );
});

export default PowerBiReport;
